import {View} from 'react-native'
import {ModerationDecision} from '@atproto/api'

import {type ClassroomContent} from '#/state/classrooms'
import {atoms as a} from '#/alf'
import {CapsulePostCard} from '#/components/capsules/CapsulePostCard'
import {ClassroomOnlyBadge} from './ClassroomOnlyBadge'
import {useClassroomCapsule} from './useClassroomCapsule'

/**
 * A classroom's own capsule, in the grid.
 *
 * It is the feed's capsule card — the same `CapsulePostCard` the capsule feed's own
 * grid draws (see `#/components/feeds/PostFeedCapsuleGridRow`) — handed a
 * post-shaped view built from the record instead of a post (see
 * `useClassroomCapsule`, which builds it) and the capsule's own address, so the
 * player opens *that* rather than whatever the feed happens to hold.
 *
 * Writing a second card here was the mistake worth naming: a hand-drawn lookalike
 * drifts. The first one had its own tile, its own gradient, its own emoji-decorated
 * level and subject, and its own idea of where the words go — so a learner in a room
 * saw a capsule that was *almost* the one at the front, and every later change to
 * the real card left this one behind.
 *
 * One thing is said here that the feed's card has no reason to say: **Classroom
 * only**. The reader cannot find this capsule on the author's profile afterwards,
 * and the badge is the difference between "it is not there" and "this is broken".
 */
export function ClassroomCapsuleCard({
  entry,
  capsuleUri,
}: {
  entry: ClassroomContent
  capsuleUri: string
}) {
  const {postView, capsule} = useClassroomCapsule({entry, capsuleUri})

  return (
    <View style={[a.relative]}>
      <CapsulePostCard
        capsulePost={postView}
        capsule={capsule}
        capsuleUri={capsuleUri}
        moderation={UNDECIDED}
      />
      <ClassroomOnlyBadge />
    </View>
  )
}

/**
 * Nothing has been said about this capsule, because nothing has seen it.
 *
 * Moderation travels with a *post*: labels, a report's outcome, a hider's
 * preference — and a capsule that belongs to one classroom has no post, so there is
 * nothing for a decision to hold. An empty decision is what the moderation library
 * itself builds when it has nothing to merge (see `ModerationDecision.merge`), and
 * the card's hider passes its content straight through, which is the honest answer
 * for a piece of work that reached exactly one screen. It becomes a real decision
 * the day classrooms live on a server and a capsule arrives with labels on it.
 */
const UNDECIDED = new ModerationDecision()
